import React, { useEffect, useState } from 'react';
import { fetchAuditoriaItem } from '../../../api/orcamentoCiliaApi';
import { AuditoriaEventoCilia, rotuloDoAutor } from '../../../interfaces/OrcamentoCilia';
import '../../auditoria/components/AuditoriaCiliaModais.css';

/**
 * Histórico de UMA peça, dentro do modal de detalhe da conferência (at-05, 16/08/2026).
 *
 * **Por que aqui e não só na aba de Auditoria**: é onde a pergunta nasce. Quem está
 * conferindo olha uma peça e quer saber quem a triou, quem a vinculou ao produto do ERP e
 * por que alguém forçou a compra dela — sem trocar de tela e sem procurar o orçamento numa
 * busca. A aba continua servindo à leitura da cadeia inteira.
 *
 * É também onde a **justificativa da compra forçada** finalmente aparece: até esta entrega
 * ela só existia no `title` de um selo, apesar de a própria tela prometer que "fica
 * registrado na auditoria".
 */

interface Props {
  itemId: string;
}

const HistoricoDaPeca: React.FC<Props> = ({ itemId }) => {
  const [eventos, setEventos] = useState<AuditoriaEventoCilia[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    fetchAuditoriaItem(itemId)
      .then((dados) => {
        if (!ativo) return;
        setEventos(dados.eventos);
        setErro(null);
      })
      .catch(() => {
        if (!ativo) return;
        /*
         * Falha NUNCA vira "nenhum registro" (regra do at-60): num histórico, lista vazia
         * por erro é indistinguível de "esta peça não foi tocada por ninguém" — e é
         * exatamente a conclusão oposta à correta. Sem toast: o modal já está aberto com o
         * conteúdo principal carregado, e o aviso na própria seção é mais preciso do que
         * um toast que fala da tela inteira.
         */
        setEventos(null);
        setErro('Não foi possível carregar o histórico desta peça. Tente novamente ao reabrir; se persistir, acione a TI.');
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [itemId]);

  if (carregando) return <div className="cilia-hist-vazio">Carregando o histórico...</div>;

  if (erro) {
    return (
      <div className="erro-de-tela" role="alert">
        {erro}
      </div>
    );
  }

  if (!eventos || eventos.length === 0) {
    return <div className="cilia-hist-vazio">Nenhuma alteração registrada para esta peça.</div>;
  }

  return (
    <div className="cilia-hist-lista">
      {eventos.map((evento) => (
        <div key={evento.id} className="cilia-hist-item">
          <div className="cilia-hist-topo">
            <span className="cilia-hist-titulo">{evento.titulo}</span>
            <span className="cilia-hist-data">
              {new Date(evento.em).toLocaleDateString('pt-BR')}{' '}
              {new Date(evento.em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          {evento.detalhes.length > 0 && (
            <div className="cilia-hist-detalhes">{evento.detalhes.join(' · ')}</div>
          )}
          <div className="cilia-hist-autor" title={rotuloDoAutor(evento).title}>Por: {rotuloDoAutor(evento).texto}</div>
        </div>
      ))}
    </div>
  );
};

export default HistoricoDaPeca;
